import Vue from "vue";
const vm = Vue.prototype;

// The manager roster comes from a LIVE Bitrix user.get, which is what makes this page slow to
// open. We cache the last-seen roster and stats in localStorage so a returning admin sees the
// page instantly, then revalidate in the background and swap only if the data actually changed
// (stale-while-revalidate). Bumping the version suffix invalidates every client's cache.
const LS_MANAGERS = "callmarkaz.managers.v1";
const LS_STATS = "callmarkaz.stats.v1";

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Storage full or unavailable — the network fetch still works, we just lose the cache.
  }
}

// Structural equality via a canonical serialization. Both payloads come from the same backend
// serializer, so key order is stable and JSON.stringify is a reliable "did anything change?".
function sameData(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

export const state = () => ({
  // Hydrated from the cache so the getters return the previous roster before any request runs.
  managers: readCache(LS_MANAGERS),
  managerStats: readCache(LS_STATS),
});

export const mutations = {
  SET_MANAGERS(state, payload) {
    state.managers = vm.$toArray(payload);
    writeCache(LS_MANAGERS, state.managers);
  },
  SET_CALL_MANAGER_STATS(state, payload) {
    state.managerStats = vm.$toArray(payload);
    writeCache(LS_STATS, state.managerStats);
  },
};

export const actions = {
  // Live Bitrix manager roster (user.get) enriched with per-manager registration counts.
  // Returns { ok, changed }: changed is false when the fresh roster equals what was cached, so
  // the caller can skip re-rendering/re-animating an unchanged page.
  async INIT_CALL_MANAGERS({ commit, state }) {
    const data = await vm.$axios.$get(`admin/call-markaz`);
    const next = vm.$toArray(data);
    const changed = !sameData(next, state.managers);
    if (changed) commit("SET_MANAGERS", data);
    return { ok: data != null, changed };
  },

  // Aggregate per-manager stats (one row per call_manager_id) for the list leaderboard.
  async INIT_CALL_MANAGER_STATS({ commit, state }) {
    const data = await vm.$axios.$get(`admin/call-markaz/stats`);
    const next = vm.$toArray(data);
    const changed = !sameData(next, state.managerStats);
    if (changed) commit("SET_CALL_MANAGER_STATS", data);
    return { ok: data != null, changed };
  },

  // Excel of the manager leaderboard. Returns a Blob.
  async EXPORT_MANAGERS() {
    return await vm.$axios.$get(`admin/call-markaz/export`, {
      responseType: "blob",
    });
  },

  // Single manager profile (id = Bitrix user id). Used by the detail page header.
  async MANAGER_BY_ID(ctx, { id }) {
    return await vm.$axios.$get(`admin/call-markaz/${id}`);
  },

  // Issue a fresh /admission desk password; returns the manager DTO with the new password.
  async GENERATE_PASSWORD(ctx, { id }) {
    return await vm.$axios.$post(`admin/call-markaz/${id}/generate-password`);
  },

  // Stat cards + conversion, optional from/to (yyyy-MM-dd) period filter.
  async MANAGER_STAT_BY_ID(ctx, { id, from, to }) {
    const params = [];
    if (from) params.push(`from=${from}`);
    if (to) params.push(`to=${to}`);
    const query = params.length ? `?${params.join("&")}` : "";
    return await vm.$axios.$get(`admin/call-markaz/${id}/stats${query}`);
  },

  // Daily acquisition buckets for the trend chart.
  async MANAGER_TIMESERIES(ctx, { id, from, to }) {
    const params = [];
    if (from) params.push(`from=${from}`);
    if (to) params.push(`to=${to}`);
    const query = params.length ? `?${params.join("&")}` : "";
    return await vm.$axios.$get(`admin/call-markaz/${id}/timeseries${query}`);
  },

  // Students attributed to this manager (the table), optional from/to window.
  async MANAGER_STUDENTS(ctx, { id, from, to }) {
    const params = [];
    if (from) params.push(`from=${from}`);
    if (to) params.push(`to=${to}`);
    const query = params.length ? `?${params.join("&")}` : "";
    return await vm.$axios.$get(`admin/call-markaz/${id}/students${query}`);
  },
};

export const getters = {
  GET_CALL_MANAGERS: (state) => state.managers,
  GET_CALL_MANAGER_STATS: (state) => state.managerStats,
};
